import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Col, ModalFooter } from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { formatDatePayload, getLocalizedName } from "components/Common/Utils"
import { showAlert } from "store/reducers/alertReducer"
import { updateGeneralDetails } from "store/actions/authAction"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import * as Yup from "yup"
import { subYears } from "date-fns"
import CustomDropdown from "components/Shared/CustomDropdown"

const initialValues = {
  first_name: "",
  middle_name: "",
  last_name: "",
  first_name_ar: "",
  middle_name_ar: "",
  last_name_ar: "",
  phone_code: "",
  phone_number: "",
  national_id: "",
  dob: "",
  email: "",
  gender: "",
  nationality_id: "",
  blood_group: "",
}

export const UpdateGeneralDetails = ({
  userDetails,
  dropdownData,
  toggleModal,
  getDetails,
  mode = "candidate",
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const [formValues, setFormValues] = useState(initialValues)
  const { isLoading } = useSelector(state => state.Login)
  const { bloodGroups } = useSelector(state => state.Common)

  useEffect(() => {
    if (userDetails) {
      const {
        first_name,
        middle_name,
        last_name,
        first_name_ar,
        middle_name_ar,
        last_name_ar,
        email,
        phone_code,
        phone_number,
        national_id,
        blood_group,
        dob,
        gender,
      } = userDetails?.user_information || {}
      const { nationality_id } = userDetails?.job_application || {}

      let formattedDate = ""

      if (typeof dob === "string") {
        const [day, month, year] = dob.split("-")
        formattedDate = new Date(`${year}-${month}-${day}`)
      }

      setFormValues(prev => ({
        first_name,
        middle_name,
        last_name,
        first_name_ar,
        middle_name_ar,
        last_name_ar,
        email,
        phone_code,
        phone_number,
        national_id,
        dob: formattedDate,
        gender,
        nationality_id,
        blood_group,
      }))
    }
  }, [userDetails])

  const handleOnSubmit = values => {
    const payload = {
      ...values,
      dob: formatDatePayload(values.dob),
    }

    const data = {
      uuid: userDetails?.user_information?.uuid,
      payload,
    }
    dispatch(updateGeneralDetails(data))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getDetails()
          toggleModal()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const isAtLeast18YearsOld = date => {
    if (!date) return false
    return subYears(new Date(), 18) >= new Date(date)
  }

  const isLessThan100YearsOld = date => {
    if (!date) return false
    return subYears(new Date(), 100) <= new Date(date)
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, t("Only English characters are allowed"))
      .required(t("Field is required")),
    middle_name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, t("Only English characters are allowed"))
      .nullable(), // Allows empty value but enforces validation if provided
    last_name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, t("Only English characters are allowed"))
      .required(t("Field is required")),
    first_name_ar: Yup.string()
      .matches(/^[\u0600-\u06FF\s]+$/, t("Only Arabic characters are allowed"))
      .required(t("Field is required")),
    middle_name_ar: Yup.string()
      .matches(/^[\u0600-\u06FF\s]+$/, t("Only Arabic characters are allowed"))
      .nullable(), // Allows empty value but enforces validation if provided
    last_name_ar: Yup.string()
      .matches(/^[\u0600-\u06FF\s]+$/, t("Only Arabic characters are allowed"))
      .required(t("Field is required")),
    dob: Yup.date()
      .required("Field is required")
      .test("is-at-least-18", t("You must be at least 18 years old"), value =>
        isAtLeast18YearsOld(value)
      )
      .test("is-less-than-100", t("Age cannot be more than 100 years"), value =>
        isLessThan100YearsOld(value)
      ),
    nationality_id: Yup.string().required(t("Field is required")),
    blood_group: Yup.string().required(t("Field is required")),
    email: Yup.string()
      .email(t("Invalid email"))
      .required(t("Field is required"))
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        t("Please enter a valid email address")
      ),
  })

  return (
    <div>
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form>
            {console.log({ values })}
            <div className="row">
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="first_name"
                    label={t("First Name(English)")}
                    isRequired={true}
                  />
                  <CustomField
                    name="first_name"
                    placeholder={t("Enter your first name(English)")}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="middle_name"
                    label={t("Middle Name(English)")}
                  />
                  <CustomField
                    name="middle_name"
                    placeholder={t("Enter your middle name(English)")}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="last_name"
                    label={t("Last Name(English)")}
                    isRequired={true}
                  />
                  <CustomField
                    name="last_name"
                    placeholder={t("Enter your last name(English)")}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="first_name_ar"
                    label={t("First Name(Arabic)")}
                    isRequired={true}
                  />
                  <CustomField
                    name="first_name_ar"
                    placeholder={t("Enter your first name(Arabic)")}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="middle_name_ar"
                    label={t("Middle Name(Arabic)")}
                  />
                  <CustomField
                    name="middle_name_ar"
                    placeholder={t("Enter your middle name(Arabic)")}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="last_name_ar"
                    label={t("Last Name(Arabic)")}
                    isRequired={true}
                  />
                  <CustomField
                    name="last_name_ar"
                    placeholder={t("Enter your last name(Arabic)")}
                    touched={touched}
                    errors={errors}
                  />
                </div>
              </div>
              <Col lg={6}>
                <div className="form_field">
                  <CustomLabel
                    htmlFor="phone_number"
                    label={t("Mobile number")}
                    isRequired={true}
                  />
                  <div className="d-flex gap-2">
                    <Field
                      as="select"
                      name="phone_code"
                      className="form-control phone_code"
                      style={{ maxWidth: 70 }}
                      disabled
                    >
                      <option value={"+966"} label={"+966"} />
                    </Field>
                    <div className="w-100">
                      <Field
                        name="phone_number"
                        type={"tel"}
                        placeholder={t("500000000")}
                        className={`form-control ${
                          errors?.phone_number && touched?.phone_number
                            ? "is-invalid"
                            : ""
                        } `}
                        readOnly={
                          mode === "candidate" &&
                          !userDetails?.can_enable_edit_profile
                        }
                      />
                      <ErrorMessage
                        name={"phone_number"}
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>
              </Col>
              <div className="col-lg-6">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="national_id"
                    label={t("National Id")}
                    isRequired={false}
                  />
                  <CustomField
                    name="national_id"
                    placeholder={t("National Id")}
                    touched={touched}
                    errors={errors}
                    isReadOnly={
                      mode === "candidate" &&
                      !userDetails?.can_enable_edit_profile
                    }
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="email"
                    label={t("Email")}
                    isRequired={true}
                  />
                  <CustomField
                    name="email"
                    placeholder={t("Email")}
                    touched={touched}
                    errors={errors}
                    isReadOnly={
                      mode === "candidate" &&
                      !userDetails?.can_enable_edit_profile
                    }
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="gender"
                    label={t("Gender")}
                    isRequired={true}
                  />
                  <div
                    role="group"
                    aria-labelledby="gender-group"
                    className="d-flex gap-4 gender_label"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={values.gender === "male"}
                        onClick={() => setFieldValue("gender", "male")}
                        style={{ height: 17, width: 17 }}
                      />
                      <label htmlFor="male" className="mb-0">
                        {t("Male")}
                      </label>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={values.gender === "female"}
                        onClick={() => setFieldValue("gender", "female")}
                        style={{ height: 17, width: 17 }}
                      />
                      <label htmlFor="female" className="mb-0">
                        {t("Female")}
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    name={"gender"}
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <Col lg={4}>
                <div className="form_field">
                  <CustomLabel
                    htmlFor="dob"
                    label={t("Date of birth")}
                    isRequired={true}
                  />
                  <Flatpickr
                    options={{ dateFormat: "d-m-Y" }} // Format: dd-mm-yyyy
                    // value={values.dob ? new Date(values.dob) : null}
                    value={values.dob}
                    onChange={selectedDates =>
                      setFieldValue("dob", selectedDates[0])
                    }
                    placeholder={t("Select a date of birth")}
                    className="form-control"
                  />
                  <ErrorMessage
                    name={"dob"}
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
              <Col lg={4}>
                <div className="form_field">
                  <CustomLabel
                    htmlFor="blood_group"
                    label={t("Blood Group")}
                    isRequired={true}
                  />
                  <CustomDropdown
                    name="blood_group"
                    value={values.blood_group}
                    options={bloodGroups}
                    optionLabel="name"
                    optionValue="name"
                    label={t("Blood Group")}
                    placeholder={t("Select Blood Group")}
                    onChange={e => setFieldValue(`blood_group`, e.value)}
                    isInvalid={touched[`blood_group`] && errors[`blood_group`]}
                  />
                  <ErrorMessage
                    name={"blood_group"}
                    component="div"
                    className="text-danger"
                  />
                </div>
              </Col>
              <div className="col-lg-4">
                <div className="form_field">
                  <CustomLabel
                    htmlFor="nationality_id"
                    label={t("Nationality")}
                    isRequired={true}
                  />
                  <div className="position-relative">
                    <Field
                      as="select"
                      name="nationality_id"
                      className="form-control"
                    >
                      <option disabled label={t("Select your nationality")} />
                      {dropdownData?.nationalities?.map(item => (
                        <option
                          value={item?.id}
                          key={item?.id}
                          label={getLocalizedName(item, language)}
                        />
                      ))}

                      {/* Add more options as needed */}
                    </Field>
                    {/* <img
                      src="/icons/select-arrow.svg"
                      width={24}
                      height={24}
                      alt=""
                      className="select_arrow"
                    /> */}
                  </div>
                  <ErrorMessage
                    name={"nationality"}
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>
              <div className="col-lg-12">
                <ModalFooter>
                  <Button color="secondary" type="button" onClick={toggleModal}>
                    {t("Close")}
                  </Button>
                  <Button color="success" type="submit" disabled={isLoading}>
                    {t("Update")}
                  </Button>
                </ModalFooter>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
